'use client'
import s from './supplier-categories-slider.module.scss'
import { SliderWithNavigation } from '@/components/v3/molecules/custom-sliders/slider-with-navigation/slider-with-navigation'
import { SupplierCategorySliderCard } from '@/components/v3/unique/supplier/supplier-category-slider-card/supplier-category-slider-card'
import { CSS_BREAKPOINTS_V2 } from '@/constants'
import { useEffect, useRef, useState } from 'react'

type SupplierCategory = {
  id: number
  path: string
  imageUrl: string
  imageAlt: string
  name: string
  videoUrl?: string
}
type Props = {
  supplierCategories: SupplierCategory[]
  title: string
}

export const SupplierCategoriesSlider = ({
  supplierCategories,
  title,
}: Props) => {
  const lastCardRef = useRef<HTMLDivElement>(null)
  const [showNavigation, setShowNavigation] = useState<boolean>(true)

  useEffect(() => {
    const node = lastCardRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const { boundingClientRect, rootBounds, isIntersecting } = entry

        if (
          !isIntersecting &&
          rootBounds &&
          boundingClientRect.right > rootBounds.right
        ) {
          setShowNavigation(true)
        } else {
          setShowNavigation(false)
        }

        observer.disconnect()
      },
      {
        root: null,
        threshold: 0.01,
      }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  const shouldShowNavigation = showNavigation || supplierCategories.length > 6
  const getPerView = (itemsPerView: number, itemsTotal: number) => {
    if (itemsTotal <= itemsPerView) {
      return itemsPerView + 1
    }
    return itemsPerView
  }

  return (
    <section className={s.container}>
      <SliderWithNavigation
        sliderControlsClassName={s.sliderControls}
        slidesWrapperClassName={s.sliderWrapper}
        keenSliderOptions={{
          slides: {
            spacing: 16,
            perView: getPerView(2.3, supplierCategories.length),
          },
          breakpoints: {
            [`(min-width: ${CSS_BREAKPOINTS_V2.mobileXL})`]: {
              slides: {
                perView: getPerView(2.6, supplierCategories.length),
                spacing: 16,
              },
            },
            [`(min-width: ${CSS_BREAKPOINTS_V2.tablet})`]: {
              slides: {
                perView: getPerView(4.7, supplierCategories.length),
                spacing: 16,
              },
            },
            [`(min-width: ${CSS_BREAKPOINTS_V2.desktopS})`]: {
              slides: {
                perView: getPerView(4.6, supplierCategories.length),
                spacing: 16,
              },
            },
            [`(min-width: ${CSS_BREAKPOINTS_V2.desktopL})`]: {
              slides: {
                perView: getPerView(6.5, supplierCategories.length),
                spacing: 16,
              },
            },
            [`(min-width: ${CSS_BREAKPOINTS_V2.desktopXL})`]: {
              slides: {
                perView: getPerView(8, supplierCategories.length),
                spacing: 16,
              },
            },
          },
        }}
        topWrapperClassName={s.topWrapper}
        title={
          <div className={s.headerContainer}>
            <h2 className={s.headerTitle}>{title}</h2>
            <span className={s.headerOrnament} />
          </div>
        }
        navigationVisible={shouldShowNavigation}
      >
        {supplierCategories.map((sc, i) => (
          <SupplierCategorySliderCard
            key={sc.id}
            ref={i === supplierCategories.length - 1 ? lastCardRef : undefined}
            href={`/wedding-suppliers/${sc.path}`}
            imageUrl={sc.imageUrl}
            imageAlt={sc.imageAlt}
            title={sc.name}
            videoUrl={sc.videoUrl}
          />
        ))}
      </SliderWithNavigation>
    </section>
  )
}
